/**
 * The API client.
 *
 * Same shape as the sibling apps: one `request` helper, typed wrappers, and
 * errors that carry the server's code so a caller can branch on it. The one
 * addition is `readEvents`, which consumes the turn endpoint's SSE stream —
 * forty lines of `TextDecoderStream` rather than a dependency, the same trade
 * website-main made for its chat widget.
 */

import type { SectionKey } from './theme';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api/scope${path}`, {
    ...init,
    // The session cookie is domain-wide; same-origin is all this needs.
    credentials: 'same-origin',
    headers: {
      accept: 'application/json',
      ...(init.body ? { 'content-type': 'application/json' } : {}),
      ...init.headers
    }
  });

  if (res.status === 204) return undefined as T;

  const body = (await res.json().catch(() => null)) as
    | (T & { error?: { code: string; message: string } })
    | null;

  if (!res.ok) {
    const error = body?.error;
    throw new ApiError(
      res.status,
      error?.code ?? 'error',
      error?.message ?? 'Something went wrong.'
    );
  }
  return body as T;
}

const post = <T>(path: string, body?: unknown): Promise<T> =>
  request<T>(path, { method: 'POST', ...(body === undefined ? {} : { body: JSON.stringify(body) }) });

// ── shapes ──────────────────────────────────────────────────────────────────

export interface Brief {
  sections: Record<SectionKey, string[]>;
  flags: { tag: string; title: string; body: string; quote: string | null }[];
  progress: number;
  askingAbout: SectionKey | null;
  suggestions: string[];
  done: boolean;
  note?: string;
}

export interface ChatMessage {
  id: string;
  seq: number;
  role: 'sme' | 'assistant';
  kind: 'chat' | 'followup';
  content: string;
  editedAt: number | null;
}

export interface IntakeStats {
  questions: number;
  answered: number;
  minutes: number;
  flags?: number;
}

export interface ResolvedLink {
  projectId: string;
  title: string;
  slug: string;
  team: string | null;
  smeName: string;
  requester: string;
}

/** A link with a password answers this until the password arrives. */
export interface LockedLink {
  needsPassword: true;
}

export interface ConversationState {
  conversation: {
    id: string;
    state: 'active' | 'submitted';
    startedAt: number;
    submittedAt: number | null;
  } | null;
  messages?: ChatMessage[];
  brief?: Brief;
  stats?: IntakeStats | null;
}

export interface ProjectSummary {
  id: string;
  title: string;
  smeName: string;
  team: string | null;
  meetingAt: number | null;
  state: string;
  createdAt: number;
  submittedAt: number | null;
  status: 'waiting' | 'opened' | 'in_progress' | 'submitted';
  unread: number;
}

export interface FlagQuestion {
  id: string;
  text: string;
  source: 'ai' | 'designer';
  checked: boolean;
  sentAt: number | null;
}

export interface DetailFlag {
  id: string | null;
  tag: string | null;
  title: string | null;
  body: string | null;
  quote: string | null;
  questions: FlagQuestion[];
}

/** How the interview runs. Mirrors worker/lib/ai/settings.ts. */
export type InterviewTone = 'formal' | 'neutral' | 'warm';

export interface InterviewSettings {
  tone: InterviewTone;
  questionGuide: string | null;
}

export interface ProjectDetail {
  project: {
    id: string;
    title: string;
    slug: string;
    smeName: string;
    team: string | null;
    meetingAt: number | null;
    state: string;
    notifyEmail: boolean;
    interview: InterviewSettings;
    createdAt: number;
  };
  link: {
    id: string;
    viewCount: number;
    createdAt: number;
    expiresAt: number | null;
    hasPassword: boolean;
  } | null;
  conversation: {
    id: string;
    state: string;
    smeName: string;
    smeVerified: boolean;
    smeHasEmail: boolean;
    startedAt: number;
    submittedAt: number | null;
    note: string | null;
  } | null;
  brief: { sections: Record<SectionKey, string[]>; progress: number } | null;
  flags: DetailFlag[];
  stats: IntakeStats | null;
  followupTail: { id: string; role: string; kind: string; content: string; createdAt: number }[];
}

// ── calls ───────────────────────────────────────────────────────────────────

export const api = {
  config: () => request<{ ai: boolean; conversations: boolean; email: boolean }>('/config'),

  projects: {
    list: () => request<{ projects: ProjectSummary[] }>('/projects'),
    detail: (id: string) => request<ProjectDetail>(`/projects/${id}`),
    create: (body: {
      title: string;
      smeName: string;
      team?: string;
      meetingAt?: number | null;
      tone?: InterviewTone;
      questionGuide?: string | null;
    }) =>
      post<{ projectId: string; slug: string; url: string; token: string; expiresAt: number }>(
        '/projects',
        body
      ),
    settings: (
      id: string,
      body: Partial<{
        title: string;
        team: string | null;
        meetingAt: number | null;
        notifyEmail: boolean;
        state: 'open' | 'archived';
        tone: InterviewTone;
        questionGuide: string | null;
      }>
    ) =>
      request<{ ok: true }>(`/projects/${id}/settings`, {
        method: 'PATCH',
        body: JSON.stringify(body)
      }),
    rotateLink: (id: string, body: { expiresInDays: number; password?: string }) =>
      post<{ url: string; token: string; expiresAt: number }>(`/projects/${id}/link`, body),
    setLinkExpiry: (id: string, expiresInDays: number) =>
      request<{ link: { expiresAt: number } }>(`/projects/${id}/link`, {
        method: 'PATCH',
        body: JSON.stringify({ expiresInDays })
      }),
    revokeLink: (id: string) => request<{ ok: true }>(`/projects/${id}/link`, { method: 'DELETE' })
  },

  questions: {
    add: (projectId: string, text: string, flagId: string | null) =>
      post<FlagQuestion>(`/projects/${projectId}/questions`, { text, flagId }),
    setChecked: (projectId: string, questionId: string, checked: boolean) =>
      request<{ ok: true }>(`/projects/${projectId}/questions/${questionId}`, {
        method: 'PATCH',
        body: JSON.stringify({ checked })
      }),
    send: (projectId: string) => post<{ sent: number }>(`/projects/${projectId}/followups/send`)
  },

  notifications: {
    list: () =>
      request<{
        unread: number;
        notifications: {
          id: string;
          kind: string;
          projectId: string;
          subject: string;
          actorName: string | null;
          readAt: number | null;
          createdAt: number;
        }[];
      }>('/notifications'),
    markRead: (id?: string) => post<{ ok: true }>('/notifications/read', id ? { id } : {})
  },

  intake: {
    resolve: (token: string, password?: string) =>
      post<ResolvedLink | LockedLink>('/public/links/resolve', { token, password }),
    identify: (token: string, name: string, email: string | null, password?: string) =>
      post<{ smeId: string; name: string }>('/public/links/identify', {
        token,
        name,
        email,
        password
      }),
    conversation: (projectId: string) =>
      request<ConversationState>(`/projects/${projectId}/conversation`),
    finish: (projectId: string) =>
      post<{ ok: true; stats: IntakeStats }>(`/projects/${projectId}/finish`),
    editInPlace: (projectId: string, messageId: string, content: string) =>
      post<{ brief: Brief }>(`/projects/${projectId}/messages/${messageId}/edit`, {
        content,
        mode: 'in_place'
      })
  }
};

// ── the turn stream ─────────────────────────────────────────────────────────

export interface TurnHandlers {
  token: (text: string) => void;
  message: (m: { id: string; seq: number }) => void;
  state: (brief: Brief) => void;
  error: (message: string) => void;
}

/**
 * POST a turn and consume its event stream.
 *
 * Events arrive as `event:`/`data:` pairs. An HTTP-level failure still answers
 * JSON, so that is unpacked into the same `error` handler the stream uses —
 * one place for the caller to render a failure.
 */
export async function streamTurn(
  path: string,
  body: unknown,
  handlers: TurnHandlers
): Promise<void> {
  const res = await fetch(`/api/scope${path}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json', accept: 'text/event-stream' },
    body: JSON.stringify(body)
  });

  if (!res.ok || !res.body) {
    const failure = (await res.json().catch(() => null)) as {
      error?: { message: string };
    } | null;
    handlers.error(failure?.error?.message ?? 'Something went wrong there. Send that again.');
    return;
  }

  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = '';
  let event = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += value;
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      if (line.startsWith('event:')) {
        event = line.slice(6).trim();
        continue;
      }
      if (!line.startsWith('data:')) continue;

      const payload = line.slice(5).trim();
      if (!payload) continue;

      let data: unknown;
      try {
        data = JSON.parse(payload);
      } catch {
        continue;
      }

      if (event === 'token') handlers.token(data as string);
      else if (event === 'message') handlers.message(data as { id: string; seq: number });
      else if (event === 'state') handlers.state(data as Brief);
      else if (event === 'error') handlers.error((data as { message: string }).message);
    }
  }
}
